import { slotId } from './text-block.js';
import { SPREAD_LAYOUTS, nearestTextPosition, resolveLayout, textCapacityFor, } from './layouts.js';
import { measureFit, wrapText } from './text-metrics.js';
/**
 * Доля вместимости слота, которую занимает комфортно свёрстанный блок.
 *
 * Калибровка: медиана 214 символов на блок в референсной вёрстке.
 * После увеличения поля до 10 мм геометрический предел при 15 pt — 43 × 7.
 * Коэффициент используется только для
 * ориентиров в брифе генератору сюжета и подсказки в редакторе — это не жёсткая
 * отсечка. Жёсткая — `fitSpreadText`: текст не помещается и самым мелким кеглем.
 *
 * ⚠️ Измерением подтверждён **только** `full-bleed`: 214 символов на блок,
 * 428 на разворот — это медиана по 26 блокам реально свёрстанной книги.
 * Значения для `split` (≈536) и `band` (≈185 при ratio 0.7) — экстраполяция
 * той же геометрии на раскладки, которых в свёрстанном виде не существует.
 * Не принимать их за измерение; перекалибровать на первых реальных книгах
 * (01-SPEC.md §3.10.6).
 */
export const COMFORTABLE_FILL_RATIO = 0.57;
export function textBudgetFor(layoutId, attributes, sizePt = attributes.typography.baseSizePt, scene) {
    const capacity = textCapacityFor(layoutId, attributes, sizePt, scene);
    const textSlots = SPREAD_LAYOUTS[layoutId].textSlots;
    const perBlock = Math.round(capacity.maxCharsPerLine * capacity.maxLines * COMFORTABLE_FILL_RATIO);
    return { capacity, perBlock, perSpread: perBlock * textSlots, textSlots };
}
/** Помещается ли текст в слот раскладки при заданном кегле. */
export function fitsInSlot(text, layoutId, attributes, sizePt = attributes.typography.baseSizePt) {
    return measureTextFit(text, layoutId, attributes, sizePt).fits;
}
/** Подробный замер: строки, вместимость и число лишних символов. */
export function measureTextFit(text, layoutId, attributes, sizePt = attributes.typography.baseSizePt) {
    return measureFit(text, textCapacityFor(layoutId, attributes, sizePt));
}
/**
 * Автоподбор кегля в коридоре `typography.autoFitPt` (01-SPEC.md §3.10.6).
 * Возвращает наибольший кегль коридора, при котором текст помещается,
 * или `null`, если не помещается и на нижней границе — тогда UI показывает
 * предупреждение с числом лишних символов. Текст не обрезается никогда.
 */
export function autoFitSizePt(text, layoutId, attributes) {
    const [minPt, maxPt] = attributes.typography.autoFitPt;
    const lo = Math.min(minPt, maxPt);
    const hi = Math.max(minPt, maxPt);
    const step = 0.5;
    for (let size = hi; size >= lo - 1e-9; size -= step) {
        const candidate = Math.round(size * 2) / 2;
        if (fitsInSlot(text, layoutId, attributes, candidate))
            return candidate;
    }
    return null;
}
/**
 * Равномерно раскладывает последовательные части по слотам, не меняя их порядок.
 * `separators[i]` — то, что стояло между частями `i` и `i + 1` в тексте автора: части,
 * попавшие в один слот, склеиваются им же, так что ни один знак не меняется.
 */
function balanceChunks(parts, count, separators) {
    const chunks = Array.from({ length: count }, () => '');
    if (parts.length === 0)
        return chunks;
    if (count <= 1)
        return [parts.map((part, i) => (i === 0 ? part : `${separators[i - 1]}${part}`)).join('')];
    if (parts.length <= count) {
        parts.forEach((part, index) => {
            chunks[index] = part;
        });
        return chunks;
    }
    const total = parts.reduce((sum, part) => sum + part.length, 0);
    let index = 0;
    let used = 0;
    parts.forEach((part, position) => {
        const partsLeft = parts.length - position;
        const chunksLeft = count - index;
        const target = (total * (index + 1)) / count;
        if (index < count - 1 && used >= target && partsLeft > chunksLeft - 1)
            index += 1;
        else if (index < count - 1 && partsLeft <= chunksLeft - 1)
            index += 1;
        const current = chunks[index] ?? '';
        chunks[index] =
            current.length > 0 ? `${current}${separators[position - 1] ?? ' '}${part}` : part;
        used += part.length;
    });
    return chunks;
}
/**
 * Текст разворота так, как его читает разбивка: пробелы внутри строк схлопнуты, а
 * переносы — авторская структура — сохранены (пустая строка выбирает блок, одинарный
 * перенос задаёт строку). Две записи с одинаковым видом свёрстываются одинаково.
 */
function normalizeSpreadText(text) {
    return text
        .replace(/\r\n?/gu, '\n')
        .split('\n')
        .map((line) => line.replace(/[^\S\n]+/gu, ' ').trim())
        .join('\n')
        .trim();
}
/** Только слова, без переносов и пробелов: меняются ли они — вопрос к картинке. */
export function textWords(text) {
    return text.replace(/\s+/gu, ' ').trim();
}
/** Разбивает текст на слоты, сохраняя явно заданные автором переносы и все знаки. */
function splitIntoChunks(text, count) {
    const normalized = normalizeSpreadText(text);
    const empty = Array.from({ length: count }, () => '');
    if (normalized.length === 0)
        return empty;
    if (count <= 1)
        return [normalized];
    // Пустая строка — явная граница текстовых блоков. Одинарные переносы
    // передаются дальше в wrapText как жёсткие.
    const paragraphs = normalized
        .split(/\n{2,}/u)
        .map((part) => part.trim())
        .filter(Boolean);
    if (paragraphs.length > 1) {
        return balanceChunks(paragraphs, count, Array(paragraphs.length - 1).fill('\n\n'));
    }
    /*
     * Sentences are cut only at a space or a single line break after a sentence's end — its
     * punctuation and whatever closes it: straight or curly quotes, guillemets, a bracket
     * (`.»`, `!”`, `…’`, `?")`) — and joined back with that same space or break, so the
     * blocks hold exactly the typed characters. A single line break is a break inside the
     * text and nothing more: the text splits between the blocks as it would without it, and
     * a break that does not end up between blocks is printed where the parent put it.
     * Matching sentences instead dropped a leading «...» and a full stop after «»», and
     * spaced out «т.е.», «(ура!)» and «Привет.Как»; curly quotes, which phones type by
     * default, must still end a sentence, or dialogue would never be cut.
     */
    const pieces = normalized.split(/(?<=[.!?…][”’»"')\]]*)( |\n)/u);
    const sentences = pieces.filter((_, i) => i % 2 === 0);
    const separators = pieces.filter((_, i) => i % 2 === 1);
    return balanceChunks(sentences, count, separators);
}
/**
 * `layoutText(textRu, layout, attributes)` — разбивка сплошного текста разворота
 * на блоки с переносами (01-SPEC.md §3.10.6).
 *
 * Переносы ставятся по смысловым границам, слова не режутся, висячих строк
 * короче трёх слов не остаётся.
 */
export function layoutText(textRu, layoutId, attributes, options = {}) {
    const layout = SPREAD_LAYOUTS[layoutId];
    const sizePt = options.sizePt ?? attributes.typography.baseSizePt;
    const capacity = textCapacityFor(layoutId, attributes, sizePt);
    const chunks = splitIntoChunks(textRu, layout.textSlots);
    const previous = options.previous ?? [];
    const blocks = [];
    for (let index = 0; index < layout.textSlots; index += 1) {
        const kept = previous[index];
        if (options.preserveManualText !== false && kept?.manual === true) {
            blocks.push({ ...kept, slotId: slotId(index) });
            continue;
        }
        const chunk = chunks[index] ?? '';
        const block = {
            slotId: slotId(index),
            text: chunk.length > 0 ? wrapText(chunk, capacity.maxCharsPerLine).join('\n') : '',
            position: nearestTextPosition(kept?.position, layoutId, index),
        };
        const textColor = kept?.textColor ?? attributes.typography.defaultTextColor;
        const highlightColor = kept?.highlightColor ?? attributes.typography.highlight;
        block.textColor = textColor;
        block.highlightColor = highlightColor;
        blocks.push(block);
    }
    return blocks;
}
function fitCandidates(attributes) {
    const base = attributes.typography.baseSizePt;
    const [a, b] = attributes.typography.autoFitPt;
    const lo = Math.min(a, b);
    const hi = Math.min(Math.max(a, b), base);
    const result = [base];
    for (let size = hi; size >= lo - 1e-9; size -= 0.5) {
        const candidate = Math.round(size * 2) / 2;
        if (!result.includes(candidate))
            result.push(candidate);
    }
    return result;
}
/**
 * Блоки разворота такими, какими их напечатает рендерер, и наибольший кегль, при
 * котором они помещаются. В отличие от `autoFitSizePt`, учитывает оба слота
 * full-bleed, ручные блоки и `bandImageRatio` сцены. Это и есть жёсткий предел
 * текста: не помещается здесь — значит, закроет картинку.
 */
export function fitSpreadText(scene, attributes) {
    const layoutId = resolveLayout(scene, attributes);
    let last = {
        blocks: [],
        fontSizePt: attributes.typography.baseSizePt,
        fits: false,
        overflowCharacters: 0,
    };
    for (const fontSizePt of fitCandidates(attributes)) {
        const blocks = layoutText(scene.textRu, layoutId, attributes, {
            previous: scene.textBlocks ?? [],
            sizePt: fontSizePt,
        });
        const capacity = textCapacityFor(layoutId, attributes, fontSizePt, scene);
        const measures = blocks.map((block) => measureFit(block.text, capacity));
        if (measures.every((measure) => measure.fits)) {
            return { blocks, fontSizePt, fits: true, overflowCharacters: 0 };
        }
        last = {
            blocks,
            fontSizePt,
            fits: false,
            overflowCharacters: measures.reduce((sum, measure) => sum + measure.overflowChars, 0),
        };
    }
    return last;
}
/**
 * Отказ в правке текста: он не помещается и самым мелким кеглем. Текст, который не
 * помещался и раньше (старая книга, раскладку уменьшили), можно сокращать по шагу,
 * но не удлинять. Мерило — длина слов, а не число лишних знаков: перенос по двум
 * блокам мог дать после удаления слова больше лишних знаков, и сокращение получало
 * отказ. Одно правило для api и редактора.
 */
export function textFitRefused(after, before) {
    if (after.fits)
        return false;
    if (!before || before.fits)
        return true;
    return spreadWords(after).length > spreadWords(before).length;
}
/** Слова, которые увидит читатель: без переносов строк и лишних пробелов. */
export function spreadWords(fit) {
    return textWords(fit.blocks.map((block) => block.text).join(' '));
}
//# sourceMappingURL=text-layout.js.map